import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses
} from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import { styled, SxProps, Theme } from '@mui/material/styles';
import AssistantCitation from 'ai-assistant/citations/assistant-citation';
import CitationSourceLink from 'ai-assistant/citations/_views/citation-source-link';
import React from 'react';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));
const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
  {
    transform: 'rotate(90deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    justifyContent: 'space-between',
    marginLeft: theme.spacing(1)
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  }),
}));
const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, 0.125)',
}));
const Title = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightMedium,
})) as typeof Typography;
const Excerpt = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  // Citation HTML contains escaped source text and evidence marks. Keep the source's
  // paragraph breaks and indentation instead of collapsing them into one paragraph.
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  lineHeight: 1.6,
  '& mark': {
    backgroundColor: 'rgba(255, 255, 0, 0.5)',
    padding: '0 2px',
    borderRadius: '2px'
  },
})) as typeof Typography;

/**
 * The DOM id of one citation panel, so a marker in the answer can scroll to it.
 *
 * Scoped to the answer it belongs to. Ordinals are turn-local by design and a conversation
 * renders every answer into one document, so keying on the ordinal alone gave every answer
 * the same set of ids: clicking a marker scrolled to the first match in the document -
 * usually a panel in an older answer - and the duplicates were invalid HTML besides.
 */
export const citationPanelId = (scope: string, ordinal: number) =>
  `assistant-citation-${scope}-${ordinal}`;

type CitationsProps = {
  className?: string;
  sx?: SxProps<Theme>;
  citations: AssistantCitation[];
  /** Distinguishes this answer's panel ids from every other answer's. */
  panelScope: string;
  /** The ordinal to show expanded, if any. Controlled by the answer's marker clicks. */
  expandedOrdinal?: number;
  onExpandedOrdinalChange?: (ordinal: number | undefined) => void;
};

export default function Citations(props: Readonly<CitationsProps>) {
  const {
    className,
    sx,
    citations,
    panelScope,
    expandedOrdinal,
    onExpandedOrdinalChange
  } = props;

  const [uncontrolledOrdinal, setUncontrolledOrdinal] = React.useState<number | undefined>(undefined);
  const isControlled = onExpandedOrdinalChange !== undefined;
  const expanded = isControlled ? expandedOrdinal : uncontrolledOrdinal;

  const handleChange = (ordinal: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    const next = isExpanded ? ordinal : undefined;
    if (isControlled) {
      onExpandedOrdinalChange(next);
      return;
    }
    setUncontrolledOrdinal(next);
  };

  return (
    <Box className={className} sx={sx}>
      <Typography variant="subtitle2" gutterBottom component="span" sx={{ ml: 1 }}>
        Citations
      </Typography>
      {citations.map((citation: AssistantCitation) => (
        <Accordion
          key={citation.key}
          id={citationPanelId(panelScope, citation.ordinal)}
          expanded={expanded === citation.ordinal}
          onChange={handleChange(citation.ordinal)}
        >
          <AccordionSummary>
            <Title component="span">
              {`${citation.ordinal}. `}
              {citation.sourceLabel}
              {citation.page ? ` (Page ${citation.page})` : null}
            </Title>
          </AccordionSummary>
          <AccordionDetails>
            {citation.withheld ? (
              /*
               * The panel still exists, because the answer text still carries the marker and a
               * marker that scrolls to nothing reads as a bug. What it says is why the excerpt
               * is not here - the answer was written when the reader could see this source and
               * the server re-checks that every time it serves the conversation back.
               */
              <Typography variant="body2" component="div" sx={{ fontStyle: 'italic' }}>
                This excerpt is no longer available to you. You could see this source when the
                answer was written; you cannot now.
              </Typography>
            ) : (
              <>
                <Typography variant="subtitle2" gutterBottom component="div">
                  Relevant Excerpt:
                </Typography>
                <Excerpt
                  component="div"
                  dangerouslySetInnerHTML={{ __html: citation.text ?? '' }}
                />
                <CitationSourceLink citation={citation} />
              </>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
